export interface LoginRequest {
    email: string
    password: string
}

export interface AuthResponse {
    token: string
    refreshToken: string
    user: {
        id: string
        name: string
        email: string
        division: string
    }
}

export interface Chatbot {
    id: string
    name: string
    description: string
    publishDate: string
    createdAt: string
    lastModified: string
    status: 'Active' | 'Inactive'
    division?: string
    embeddedUrl?: string
}

export interface ChatbotFile {
    id: string
    filename: string
    size: number
    created_at: string
}

export type IngestionTaskStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'NO_TASK'

export interface IngestionTask {
    id: number
    chatbot_id: number
    document_id: number
    document_name?: string | null
    status: IngestionTaskStatus
    error_message?: string | null
    created_at?: string | null
    updated_at?: string | null
}

export interface ChatbotIngestionTasksResponse {
    chatbot_id: number
    chatbot_name: string
    latest_per_file: boolean
    status_filter: IngestionTaskStatus | null
    count: number
    counts: Record<'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED', number>
    tasks: IngestionTask[]
}

export interface TaskPollingResponse {
    chatbot_id: number
    success_tasks: number
    total_tasks: number
}

export interface FileIngestionTaskResponse {
    file_id: number
    chatbot_id: number
    status: IngestionTaskStatus
    task: IngestionTask | null
    tasks?: IngestionTask[]
}

export interface QnAFile {
    id: string
    name: string
    lastUpdate: string
}

export interface ApiError {
    message: string
    status: number
}

class ApiClient {
    private authBaseUrl: string  // For auth functions (Static Web App)
    private backendBaseUrl: string  // For main backend (Flask/external)

    constructor() {
        this.authBaseUrl = '/api/auth'  // Static Web App managed functions
        this.backendBaseUrl = `${process.env.BUN_PUBLIC_API_URL}/admin`  // Flask backend for admin functions
    }

    private async refreshToken(): Promise<string> {
        const refreshToken = localStorage.getItem('refreshToken')
        if (!refreshToken) throw new Error('No refresh token available')

        const response = await fetch(`${this.authBaseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken })
        })

        if (!response.ok) throw new Error('Failed to refresh token')

        const data = await response.json()
        localStorage.setItem('token', data.token)
        return data.token
    }

    private async request(endpoint: string, options: RequestInit = {}, useBackend: 'auth' | 'backend' = 'backend') {
        const token = localStorage.getItem('token')

        const headers: Record<string, string> = token
            ? { 'Authorization': `Bearer ${token}` }
            : {}

        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json'
        }

        const config = {
            ...options,
            headers: {
                ...headers,
                ...options.headers,
            },
        }
        // console.log(`API Request: this.backendBaseUrl=${this.backendBaseUrl}, config=`, config)

        const baseUrl = useBackend === 'auth' ? this.authBaseUrl : this.backendBaseUrl
        const url = `${baseUrl}${endpoint}`


        let response = await fetch(url, config)

        // Handle token expiration
        if (response.status === 401 && endpoint !== '/login' && endpoint !== '/auth/refresh') {
            try {
                const newToken = await this.refreshToken()

                // Retry request with new token
                const newHeaders = {
                    ...config.headers,
                    'Authorization': `Bearer ${newToken}`
                }

                // Re-fetch with new token
                response = await fetch(url, {
                    ...config,
                    headers: newHeaders
                })
            } catch (error) {
                // Session expired, clear storage and redirect to login
                localStorage.removeItem('token')
                localStorage.removeItem('refreshToken')
                localStorage.removeItem('user')
                window.location.href = '/login'
                throw error
            }
        }
        return response
    }

    async login(data: LoginRequest): Promise<AuthResponse> {
        const response = await this.request('/login', {
            method: 'POST',
            body: JSON.stringify(data),
        }, 'auth')

        if (!response.ok) {
            const error: ApiError = {
                message: 'Invalid credentials',
                status: response.status,
            }
            throw error
        }

        return response.json()
    }

    async getCurrentUser(token: string) {
        const response = await this.request('/me', {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        }, 'auth')

        if (!response.ok) {
            throw new Error('Failed to get user')
        }

        return response.json()
    }

    async getChatbots(signal?: AbortSignal): Promise<Chatbot[]> {
        const response = await this.request('/chatbots', { signal })
        if (!response.ok) throw new Error('Failed to fetch chatbots')
        return response.json()
    }

    async getChatbot(id: string): Promise<Chatbot | undefined> {
        const response = await this.request(`/chatbots/${id}`)
        if (!response.ok) return undefined
        return response.json()
    }

    async saveChatbot(data: Partial<Chatbot> & { name: string }): Promise<any> {
        if (data.id && data.id !== 'Auto-generated' && !data.id.startsWith('Auto')) {
            const response = await this.request(`/chatbots/${data.id}`, {
                method: 'PUT',
                body: JSON.stringify(data)
            })
            if (!response.ok) throw new Error('Failed to update chatbot')
            return response.json()
        } else {
            const response = await this.request('/chatbots', {
                method: 'POST',
                body: JSON.stringify(data)
            })
            if (!response.ok) throw new Error('Failed to create chatbot')
            return response.json()
        }
    }

    async updateChatbotStatus(id: string, status: 'Active' | 'Inactive'): Promise<void> {
        const response = await this.request(`/chatbots/${id}/status`, {
            method: 'PATCH',
            body: JSON.stringify({ status })
        })
        if (!response.ok) throw new Error('Failed to update status')
    }

    async getChatbotFiles(chatbotId: string): Promise<ChatbotFile[]> {
        const response = await this.request(`/chatbots/${chatbotId}/files`)
        if (!response.ok) throw new Error('Failed to fetch chatbot files')
        return response.json()
    }

    async uploadChatbotFile(chatbotId: string, file: File): Promise<ChatbotFile> {
        const formData = new FormData()
        formData.append('file', file)
        const response = await this.request(`/chatbots/${chatbotId}/files`, {
            method: 'POST',
            body: formData,
        })
        if (!response.ok) {
            let errorMsg = 'Failed to upload file'
            try {
                const errorData = await response.json()
                if (errorData.error) errorMsg = errorData.error
            } catch (e) { }
            throw new Error(errorMsg)
        }
        return response.json()
    }

    async replaceChatbotFile(chatbotId: string, fileId: string, file: File): Promise<ChatbotFile> {
        const formData = new FormData()
        formData.append('file', file)
        const response = await this.request(`/chatbots/${chatbotId}/files/${fileId}`, {
            method: 'PUT',
            body: formData,
        })
        if (!response.ok) {
            let errorMsg = 'Failed to replace file'
            try {
                const errorData = await response.json()
                if (errorData.error) errorMsg = errorData.error
            } catch (e) { }
            throw new Error(errorMsg)
        }
        return response.json()
    }

    async deleteChatbotFile(chatbotId: string, fileId: string): Promise<void> {
        const response = await this.request(`/chatbots/${chatbotId}/files/${fileId}`, {
            method: 'DELETE',
        })
        if (!response.ok) throw new Error('Failed to delete file')
    }

    async getChatbotIngestionTasksStatus(
        chatbotId: string,
        options?: {
            latestPerFile?: boolean
            status?: Exclude<IngestionTaskStatus, 'NO_TASK'>
            type?: 'KNOWLEDGE_BASE' | 'QNA'
        }
    ): Promise<ChatbotIngestionTasksResponse> {
        const params = new URLSearchParams()
        if (options?.latestPerFile !== undefined) {
            params.set('latest_per_file', String(options.latestPerFile))
        }
        if (options?.status) {
            params.set('status', options.status)
        }
        if (options?.type) {
            params.set('type', options.type)
        }

        const query = params.toString()
        const response = await this.request(`/chatbots/${chatbotId}/tasks${query ? `?${query}` : ''}`)
        if (!response.ok) throw new Error('Failed to fetch chatbot ingestion tasks status')
        return response.json()
    }

    async getChatbotFileIngestionTaskStatus(
        chatbotId: string,
        fileId: string,
        options?: { includeHistory?: boolean }
    ): Promise<FileIngestionTaskResponse> {
        const params = new URLSearchParams()
        if (options?.includeHistory !== undefined) {
            params.set('include_history', String(options.includeHistory))
        }

        const query = params.toString()
        const response = await this.request(`/chatbots/${chatbotId}/files/${fileId}/tasks${query ? `?${query}` : ''}`)
        if (!response.ok) throw new Error('Failed to fetch file ingestion task status')
        return response.json()
    }

    async getAllChatbotsIngestionPollingMap(): Promise<TaskPollingResponse[]> {
        const response = await this.request('/tasks/polling')
        if (!response.ok) throw new Error('Failed to fetch chatbot ingestion polling map')
        const data = await response.json()

        if (Array.isArray(data)) {
            return data
        }

        const wrappedData = data
        if (wrappedData && Array.isArray(wrappedData.chatbots)) {
            return wrappedData.chatbots
        }

        return []
    }

    async getChatbotQnAFiles(chatbotId: string): Promise<QnAFile[]> {
        const response = await this.request(`/chatbots/${chatbotId}/qna`)
        if (!response.ok) throw new Error('Failed to fetch QnA files')
        return response.json()
    }

    async uploadChatbotQnAFile(chatbotId: string, file: File): Promise<void> {
        const formData = new FormData()
        formData.append('file', file)
        const response = await this.request(`/chatbots/${chatbotId}/qna`, {
            method: 'POST',
            body: formData,
        })
        if (!response.ok) {
            let errorMsg = 'Failed to upload QnA file'
            try {
                const errorData = await response.json()
                if (errorData.error) errorMsg = errorData.error
            } catch (e) { }
            throw new Error(errorMsg)
        }
    }

    async deleteChatbotQnAFile(chatbotId: string, fileId: string): Promise<void> {
        const response = await this.request(`/chatbots/${chatbotId}/qna/${fileId}`, {
            method: 'DELETE',
        })
        if (!response.ok) throw new Error('Failed to delete QnA file')
    }

    async exportLogs(chatbotId: string, startDate: string, endDate: string): Promise<Blob> {
        const params = new URLSearchParams({
            chatbot_id: chatbotId,
            start_date: startDate,
            end_date: endDate,
            tz_offset: new Date().getTimezoneOffset().toString()
        })
        const response = await this.request(`/logs/export?${params.toString()}`, {
            method: 'GET',
        })
        if (!response.ok) throw new Error('Failed to export logs')
        return response.blob()
    }
}

export const api = new ApiClient()
